import { useEffect, useRef, useState } from "react";
import { nativePost } from "@/utils/nativeHttp";
import { showErrorToast } from "@/utils/toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Apple Pay (iOS/Safari) and Google Pay (Android/Chrome) through Stripe's
// Payment Request API, shared by the checkout modal and the paywall.
//
// Creates the paymentRequest for `amount` (updated when the amount changes),
// resolves whether the device has a wallet, and on a wallet payment creates
// the intent with `intentBody(payerEmail)` + `authHeaders()`, confirms it,
// then runs `onSuccess` (the delivery) and `onClose`. Progress is reported
// through setStage ("paying" | "delivering" | "delivered" | "deliverFailed" |
// "form") and setError, the same states the checkout modal uses.
//
// Returns { paymentRequest, walletStatus }: walletStatus is "checking" until
// Stripe answers, then "available" (render the button) or "unavailable".
export function useWalletPayment({ stripe, enabled = true, label, amount, intentBody, authHeaders, onSuccess, onClose, setStage, setError }) {
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [walletStatus, setWalletStatus] = useState("checking");
  // The handlers read the latest props without re-creating the request
  const latest = useRef({});
  latest.current = { intentBody, authHeaders, onSuccess, onClose, setStage, setError };

  useEffect(() => {
    if (!stripe || !enabled) { setWalletStatus("unavailable"); return undefined; }
    let pr;
    try {
      pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: { label: `MintSlip: ${label}`, amount: Math.round(amount * 100) },
        requestPayerName: true,
        requestPayerEmail: true,
      });
    } catch { setWalletStatus("unavailable"); return undefined; }
    let cancelled = false;
    pr.canMakePayment().then((result) => {
      if (cancelled) return;
      if (result) { setPaymentRequest(pr); setWalletStatus("available"); }
      else setWalletStatus("unavailable");
    }).catch(() => { if (!cancelled) setWalletStatus("unavailable"); });
    pr.on("paymentmethod", async (ev) => {
      const { intentBody: body, authHeaders: headers, onSuccess: success, onClose: close, setStage: stage, setError: error } = latest.current;
      error("");
      stage("paying");
      try {
        const { ok, data } = await nativePost(`${BACKEND_URL}/api/stripe/create-payment-intent`, body(ev.payerEmail), headers());
        if (!ok || !data?.clientSecret) { ev.complete("fail"); throw new Error(data?.detail || "Could not start the payment. Please try again."); }
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          data.clientSecret, { payment_method: ev.paymentMethod.id }, { handleActions: false }
        );
        if (confirmError) { ev.complete("fail"); throw new Error(confirmError.message || "Payment failed. Please try again."); }
        ev.complete("success");
        let pi = paymentIntent;
        if (pi.status === "requires_action") {
          const followUp = await stripe.confirmCardPayment(data.clientSecret);
          if (followUp.error) throw new Error(followUp.error.message || "Payment authentication failed.");
          pi = followUp.paymentIntent;
        }
        if (pi.status !== "succeeded") throw new Error("Payment did not complete. Please try again.");
        stage("delivering");
        try {
          await success({ email: ev.payerEmail || "", name: ev.payerName || "", paymentIntentId: pi.id });
          stage("delivered");
          close();
        } catch (deliverErr) {
          console.error("Post-payment delivery failed:", deliverErr);
          stage("deliverFailed");
        }
      } catch (err) {
        const msg = err.message || "Payment failed. Please try again.";
        // Banned customers get the ban reason as a prominent toast too
        if (/banned from using MintSlip/i.test(msg)) showErrorToast(msg, { duration: 6000 });
        error(msg);
        stage("form");
      }
    });
    return () => { cancelled = true; };
  }, [stripe, enabled]); // eslint-disable-line

  // Keep the sheet's total in step with the price shown (e.g. an offer ending)
  useEffect(() => {
    if (!paymentRequest) return;
    try { paymentRequest.update({ total: { label: `MintSlip: ${label}`, amount: Math.round(amount * 100) } }); } catch { /* sheet open */ }
  }, [paymentRequest, amount, label]);

  return { paymentRequest, walletStatus };
}
